import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import { Button } from "@/components/ui/button";
import { Phone, ArrowRight } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { SITE_URL } from "@/hooks/use-seo";
import {
  CLINIC_REF,
  buildBreadcrumbList,
  Crumb,
} from "@/components/schemaConstants";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "./RelatedPages";
import EligibilityCheckForm from "./EligibilityCheckForm";
import ClinicalReviewNote from "./ClinicalReviewNote";
export interface SeoConfig {
  title: string;
  description: string;
  canonical: string;
  keywords?: string;
  ogImage?: string;
  jsonLd?: object;
  breadcrumbs?: Crumb[];
  extraSchema?: object | object[];
}

/**
 * When a decision page doesn't supply its own JSON-LD, generate a
 * MedicalWebPage block so every page has structured data for search engines.
 * The provider references the sitewide Organization entity by @id.
 */
function buildDefaultJsonLd(seo: SeoConfig): object[] {
  const canonicalPath = seo.canonical.replace(/\/+$/, "") || "/";
  const url = `${SITE_URL.replace(/\/$/, "")}${canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`}`;
  const blocks: object[] = [
    {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      name: seo.title.split("|")[0].trim(),
      description: seo.description,
      url,
      provider: CLINIC_REF,
    },
  ];
  if (seo.breadcrumbs && seo.breadcrumbs.length > 0) {
    blocks.push(buildBreadcrumbList(seo.breadcrumbs, canonicalPath));
  }
  if (seo.extraSchema) {
    const extra = Array.isArray(seo.extraSchema)
      ? seo.extraSchema
      : [seo.extraSchema];
    blocks.push(...extra);
  }
  return blocks;
}
interface DecisionPageLayoutProps {
  seo: SeoConfig;
  badge: string;
  title: string;
  subtitle: string;
  children: React.ReactNode;
  ctaHeading?: string;
  ctaText?: string;
  /** E-E-A-T: named medical reviewer (e.g. "Daniel Pietras, MD"). */
  reviewedBy?: string;
  /** E-E-A-T: ISO 8601 date of last clinical review. */
  lastUpdated?: string;
  /** When true, renders the EligibilityCheckForm beside the hero headline
   *  in a conversion-first split layout (like /saint-google). */
  heroForm?: boolean;
  heroFormSource?: string;
}
export const DecisionPageLayout: React.FC<DecisionPageLayoutProps> = ({
  seo,
  badge,
  title,
  subtitle,
  children,
  ctaHeading = "Schedule a Free Phone Consultation",
  ctaText = "No commitment. We'll review your situation, answer your questions, and help you understand whether SAINT or another treatment is the right fit. One phone call — (954) 827-9793.",
  reviewedBy,
  lastUpdated,
  heroForm = false,
  heroFormSource = "saint-decision-page",
}) => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        ogImage={seo.ogImage}
        keywords={seo.keywords}
        schemaJson={seo.jsonLd || buildDefaultJsonLd(seo)}
      />

      <Navbar />
      <main className="pt-16 sm:pt-20">
        {/* Hero — clean navy background */}
        <section className="relative text-primary-foreground py-14 sm:py-24 overflow-hidden bg-primary">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/95 via-primary/90 to-primary-deep/90" />
          <div
            className={
              heroForm
                ? "container-px relative mx-auto max-w-6xl px-4 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center"
                : "container-px relative mx-auto max-w-5xl px-4"
            }
          >
            <div className="max-w-xl text-left space-y-5 order-2 lg:order-1">
              <div className="flex flex-wrap items-center gap-3">
                {badge && (
                  <span className="inline-block rounded-full bg-accent/90 backdrop-blur-sm px-4 py-1.5 text-xs sm:text-sm font-bold text-white shadow-lg uppercase tracking-wider">
                    {badge}
                  </span>
                )}
                <div className="inline-flex items-center rounded-xl bg-white px-2.5 py-1 shadow-sm border border-border">
                  <img
                    src="https://vibe.filesafe.space/1787327131284421342/attachments/861c17b1-d72f-46dc-9f82-4d1ea308284e.png"
                    alt="SAINT by Magnus Medical"
                    className="h-6 sm:h-7 w-auto object-contain"
                    width={140}
                    height={35}
                    loading="lazy"
                  />
                </div>
              </div>
              <h1 className="font-headings text-2xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight drop-shadow-md">
                {title}
              </h1>
              <p className="text-base sm:text-lg text-primary-foreground/90 leading-relaxed drop-shadow-md">
                {subtitle}
              </p>
              {!heroForm && (
                <div className="flex flex-col sm:flex-row items-start justify-start gap-4 pt-3">
                  <Button
                    asChild
                    size="lg"
                    className="w-full sm:w-auto bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90 text-base px-8 py-6"
                  >
                    <a href="/#consultation">Check Insurance Options</a>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto border-2 border-white/60 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 hover:border-white/80 text-base px-8 py-6"
                  >
                    <a href="tel:9548279793">
                      <Phone className="h-5 w-5 mr-2" />
                      Call 954-827-9793
                    </a>
                  </Button>
                </div>
              )}
            </div>
            {heroForm && (
              <div className="w-full order-1 lg:order-2">
                <EligibilityCheckForm
                  source={heroFormSource}
                  redirectTo="/thank-you-saint-google"
                />
              </div>
            )}
          </div>
        </section>

        {/* Breadcrumbs */}
        {seo.breadcrumbs && seo.breadcrumbs.length > 0 && (
          <div className="bg-background py-3">
            <div className="container-px mx-auto max-w-3xl px-4">
              <Breadcrumbs crumbs={seo.breadcrumbs} />
            </div>
          </div>
        )}

        {/* Content */}
        <section className="py-10 sm:py-16 bg-background">
          <div className="container-px mx-auto max-w-3xl px-4">
            {(reviewedBy || lastUpdated) && (
              <ClinicalReviewNote
                reviewedBy={reviewedBy}
                lastUpdated={lastUpdated}
                className="mb-6"
              />
            )}
            {children}
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 sm:py-16 bg-gradient-navy">
          <div className="container-px mx-auto max-w-2xl px-4 text-center space-y-5">
            <h2 className="font-headings text-2xl sm:text-3xl font-bold text-white">
              {ctaHeading}
            </h2>
            <p className="text-primary-foreground/80 text-base sm:text-lg leading-relaxed">
              {ctaText}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Button
                asChild
                size="lg"
                className="w-full sm:w-auto bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                <a href="/#consultation">Schedule a Free Phone Consultation</a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-2 border-white/50 bg-transparent text-white hover:bg-white/15 hover:border-white/70"
              >
                <a href="tel:9548279793">
                  <Phone className="h-4 w-4 mr-2" />
                  (954) 827-9793
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

// Reusable sub-components for decision pages
export const DecisionSection: React.FC<{
  heading?: string;
  children: React.ReactNode;
  className?: string;
}> = ({ heading, children, className = "" }) => (
  <div className={`space-y-3 ${className}`}>
    {heading && (
      <h2 className="font-headings text-xl sm:text-2xl font-bold text-primary">
        {heading}
      </h2>
    )}
    <div className="space-y-3 text-foreground/85 text-base sm:text-lg leading-relaxed">
      {children}
    </div>
  </div>
);
export const DecisionSubHeading: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <h3 className="font-headings text-lg sm:text-xl font-bold text-primary pt-4">
    {children}
  </h3>
);
export const DecisionCard: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = "" }) => (
  <div
    className={`rounded-2xl bg-card border border-border p-5 sm:p-7 shadow-soft space-y-3 ${className}`}
  >
    {children}
  </div>
);
export const DecisionList: React.FC<{
  items: string[];
}> = ({ items }) => (
  <ul className="space-y-2">
    {items.map((item, i) => (
      <li
        key={i}
        className="flex gap-3 text-foreground/85 text-base leading-relaxed"
      >
        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);
export const CrisisBanner: React.FC<{
  variant?: "light" | "dark";
}> = ({ variant = "light" }) => {
  const isDark = variant === "dark";
  return (
    <div
      className={`rounded-xl border p-4 text-center ${isDark ? "bg-white/10 border-white/20 text-white" : "bg-light-blue/40 border-light-blue text-primary"}`}
    >
      <p
        className={`text-sm sm:text-base font-medium ${isDark ? "text-white/90" : "text-primary"}`}
      >
        If you're in crisis, call or text{" "}
        <a
          href="tel:988"
          className="font-bold underline decoration-accent decoration-2 underline-offset-2"
        >
          988
        </a>{" "}
        (Suicide & Crisis Lifeline, 24/7) or call{" "}
        <a
          href="tel:911"
          className="font-bold underline decoration-accent decoration-2 underline-offset-2"
        >
          911
        </a>{" "}
        or go to your nearest emergency room.
      </p>
    </div>
  );
};

/**
 * Compact inline link list — for DecisionPageLayout pages that use a narrow
 * single-column content area.
 */
export const InlineRelatedLinks: React.FC<{
  heading?: string;
  links: RelatedLink[];
}> = ({ heading = "Explore Related Pages", links }) => {
  if (!links || links.length === 0) return null;
  return (
    <div className="mt-8 rounded-2xl border border-border bg-card p-5 sm:p-7 shadow-soft space-y-4">
      <h2 className="font-headings text-lg sm:text-xl font-bold text-primary">
        {heading}
      </h2>
      <div className="flex flex-col gap-2">
        {links.map((link, idx) => (
          <Link
            key={idx}
            to={link.href}
            className="inline-flex items-center gap-2 text-base font-medium text-accent hover:underline underline-offset-2"
          >
            <ArrowRight className="h-4 w-4 shrink-0" />
            {link.title}
          </Link>
        ))}
      </div>
    </div>
  );
};
